import {
    Box,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { Info } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { STRUCTURE_SET_NOT_SHOWN_HINT_ID } from '~/hooks/target/structureSetNotShown';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    getContourUndoService,
    getRoiBaselineStore,
} from '~/domain/ServiceContainer';
import {
    DifferenceReferenceMode,
    differenceReferenceModeState,
} from '~/state/atoms/toolsAtoms';

/**
 * Data a difference mode needs before it can be selected: a captured Contour+
 * baseline for 'original-ai', a prior undo snapshot for 'pre-operation'. The
 * cross-slice and 'off' modes always apply.
 */
type DifferenceModeRequirement = 'baseline' | 'previous-op';

interface DifferenceModeOption {
    mode: DifferenceReferenceMode;
    labelId: string;
    requires?: DifferenceModeRequirement;
    unavailableHintId?: string;
}

/** Difference-overlay reference options, in menu order. */
const DIFFERENCE_MODE_OPTIONS: DifferenceModeOption[] = [
    { mode: 'off', labelId: 'toolbar.navigation.difference.off' },
    {
        mode: 'previous-slice',
        labelId: 'toolbar.navigation.difference.previousSlice',
    },
    {
        mode: 'pre-operation',
        labelId: 'toolbar.navigation.difference.preOperation',
        requires: 'previous-op',
        unavailableHintId:
            'toolbar.navigation.difference.preOperationUnavailable',
    },
    {
        mode: 'original-ai',
        labelId: 'toolbar.navigation.difference.originalAi',
        requires: 'baseline',
        unavailableHintId:
            'toolbar.navigation.difference.originalAiUnavailable',
    },
];

/**
 * Tray body for the "Differences" viewer tool: a radio picker that chooses which
 * prior version of the SELECTED structure the difference overlay highlights
 * against (green = added, red = removed) — the previous slice, its state before
 * the last operation, or its original AI prediction — or 'off'.
 *
 * Every drawing mode needs the structure set on screen: the overlay is drawn
 * on the views of the Environment holding the set, and a set that sits on no
 * view has nothing to draw on, so every mode but 'off' is disabled with that
 * reason rather than lit without effect. The two version-diff modes further
 * need a reference to exist for the ROI: 'original-ai' a captured AI baseline
 * (an AI-generated structure that has been edited), 'pre-operation' a prior
 * undo snapshot. A disabled radio stays visible, so the option remains
 * discoverable, with an info icon beside it whose tooltip explains why it is
 * unavailable.
 *
 * Display aid only: it writes {@link differenceReferenceModeState}; the overlay
 * itself is drawn by the always-mounted {@link useReferenceDiff}. Every mode
 * diffs the *selected* ROI, so the footer names it to keep the scope explicit.
 */
export default function DifferenceOverlayOptions() {
    const intl = useIntl();
    const [mode, setMode] = useRecoilState(differenceReferenceModeState);
    const {
        environment,
        structureSet,
        roiNumber: selectedRoiNumber,
    } = useActiveEditTarget();
    const selectedRoiName = structureSet?.roiArray.find(
        (roi) => roi.roiNumber === selectedRoiNumber
    )?.name;

    // A version-diff mode is only offered when its reference exists for the
    // selected ROI. Read from the domain services (not Recoil); the picker
    // re-mounts whenever it opens, so a fresh read reflects the ROI's state. The
    // baseline lookup is scoped by structure-set UID so a same-numbered ROI in
    // another loaded set can't surface the wrong set's baseline.
    const hasBaseline =
        getRoiBaselineStore().get(
            structureSet?.dicomUid ?? '',
            selectedRoiNumber
        ) !== undefined;
    const hasPreviousOperation = getContourUndoService().canUndo(
        structureSet?.dicomUid ?? '',
        selectedRoiNumber
    );
    const hasRequirement = (
        requires: DifferenceModeRequirement | undefined
    ): boolean => {
        if (requires === 'baseline') {
            return hasBaseline;
        }
        if (requires === 'previous-op') {
            return hasPreviousOperation;
        }
        return true;
    };
    // The message id explaining why a mode cannot be picked; undefined when
    // it can. 'off' always can: the display aid must always be clearable.
    const unavailableHintIdOf = (
        option: DifferenceModeOption
    ): string | undefined => {
        if (option.mode === 'off') {
            return undefined;
        }
        if (!environment) {
            return STRUCTURE_SET_NOT_SHOWN_HINT_ID;
        }
        return hasRequirement(option.requires)
            ? undefined
            : option.unavailableHintId;
    };

    return (
        <Box sx={{ minWidth: 180 }}>
            <RadioGroup
                value={mode}
                onChange={(event) =>
                    setMode(event.target.value as DifferenceReferenceMode)
                }
            >
                {DIFFERENCE_MODE_OPTIONS.map((option) => {
                    const unavailableHintId = unavailableHintIdOf(option);
                    const available = unavailableHintId === undefined;
                    // Explain a disabled mode with an info icon + tooltip rather
                    // than inline text, kept next to the label as a sibling of the
                    // (disabled) control so it stays hoverable/focusable.
                    const unavailableHint = unavailableHintId
                        ? intl.formatMessage({ id: unavailableHintId })
                        : undefined;
                    return (
                        <Box
                            key={option.mode}
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            <FormControlLabel
                                value={option.mode}
                                disabled={!available}
                                control={
                                    <Radio size="small" sx={{ py: 0.25 }} />
                                }
                                label={
                                    <Typography sx={{ fontSize: 12 }}>
                                        {intl.formatMessage({
                                            id: option.labelId,
                                        })}
                                    </Typography>
                                }
                                sx={{ mr: 0.25 }}
                            />
                            {unavailableHint && (
                                <Tooltip
                                    title={unavailableHint}
                                    placement="right"
                                >
                                    <Box
                                        component="span"
                                        tabIndex={0}
                                        aria-label={unavailableHint}
                                        sx={{
                                            display: 'inline-flex',
                                            ml: 0.75,
                                            opacity: 0.6,
                                            cursor: 'help',
                                        }}
                                    >
                                        <Info size={13} aria-hidden />
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                    );
                })}
            </RadioGroup>
            <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.5 }}>
                {selectedRoiName ? (
                    <FormattedMessage
                        id="toolbar.navigation.difference.forRoi"
                        values={{ roi: selectedRoiName }}
                    />
                ) : (
                    <FormattedMessage id="toolbar.navigation.difference.noRoi" />
                )}
            </Typography>
        </Box>
    );
}
